import React, { forwardRef, InputHTMLAttributes, useState } from 'react';
import clsx from 'clsx';
import MaskedInput, { MaskedInputProps } from 'react-text-mask';
import styles from './Input.module.scss';
import Icon from '../Icon';
import Text from '../Text/Text';
import Search from '../../../public/search-normal.svg';

export enum InputVariant {
  DEFAULT = 'default',
  ERROR = 'error',
  WARNING = 'warning',
  SUCCESS = 'success',
}

type PipeFunction = (
  conformedValue: string,
  config: any
) => string | false | { value: string; indexesOfPipedChars: number[] };

type MaskType = Array<string | RegExp> | ((value: string) => Array<string | RegExp>);

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'className' | 'onChange'> {
  label?: string;
  variant?: InputVariant;
  disabled?: boolean;
  className?: string;
  showIcon?: boolean;
  textArea?: false;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  isWhite?: boolean;
  errorText?: string;
  isSearch?: boolean;
  isPassword?: boolean;
  mask?: MaskType;
  guide?: boolean;
  placeholderChar?: string;
  keepCharPositions?: boolean;
  pipe?: PipeFunction;
  showMask?: boolean;
  iconClassName?: string;
}

export interface TextAreaProps extends Omit<React.TextareaHTMLAttributes<HTMLTextAreaElement>, 'className' | 'onChange'> {
  label?: string;
  variant?: InputVariant;
  disabled?: boolean;
  className?: string;
  showIcon?: boolean;
  textArea: true;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLTextAreaElement>) => void;
  isWhite?: boolean;
  errorText?: string;
  isSearch?: boolean;
  isPassword?: boolean;
}

const Input = forwardRef<HTMLInputElement | HTMLTextAreaElement, InputProps | TextAreaProps>(
  (
    {
      id,
      label,
      variant = InputVariant.DEFAULT,
      disabled = false,
      className = '',
      showIcon = true,
      textArea = false,
      value,
      onChange,
      isWhite = false,
      errorText,
      isSearch = false,
      isPassword = false,
      ...props
    },
    ref,
  ) => {
    // Auto-detect variant based on error text
    const effectiveVariant = errorText ? InputVariant.ERROR : variant;

    const getIconClassName = () => {
      switch (effectiveVariant) {
        case InputVariant.ERROR:
          return styles.errorIcon;
        case InputVariant.WARNING:
          return styles.warningIcon;
        case InputVariant.SUCCESS:
          return styles.successIcon;
        default:
          return '';
      }
    };

    const getVariantClass = () => {
      switch (effectiveVariant) {
        case InputVariant.ERROR:
          return styles.error;
        case InputVariant.WARNING:
          return styles.warning;
        case InputVariant.SUCCESS:
          return styles.success;
        default:
          return '';
      }
    };

    const variantClass = getVariantClass();
    const inputClasses = clsx(
      styles.input,
      variantClass,
      disabled && styles.disabled,
      isWhite && styles.white,
      className,
    );

    const containerClasses = clsx(
      styles.inputContainer,
      showIcon && styles.withIcon,
    );

    const getIconName = () => {
      switch (effectiveVariant) {
        case InputVariant.ERROR:
          return 'error';
        case InputVariant.WARNING:
          return 'warning';
        case InputVariant.SUCCESS:
          return 'check_circle';
        default:
          return '';
      }
    };

    const onClear = () => {
      if (!onChange) return;

      const event = {
        target: { value: '' },
        currentTarget: { value: '' },
      } as any;

      onChange(event);
    };

    const showClear = isSearch && !disabled && !textArea && value && value.length > 0;

    const [showPassword, setShowPassword] = useState(false);

    const computedType = isPassword ? (showPassword ? 'text' : 'password') : (props as InputProps).type || 'text';

    const inputProps = props as InputProps;
    const {
      mask,
      guide = true,
      placeholderChar = '_',
      keepCharPositions = false,
      pipe,
      showMask = false,
      iconClassName,
      ...restProps
    } = inputProps;

    const hasMask = !textArea && !!mask;

    const maskProps: Partial<MaskedInputProps> = hasMask && mask ? {
      mask,
      guide,
      placeholderChar,
      keepCharPositions,
      showMask,
      pipe,
    } : {};

    return (
      <div className={clsx(styles.inputWrapper, { [styles.isSearch]: isSearch })}>
        {label && (
          <label
            htmlFor={id}
            className={styles.label}
          >
            {label}
          </label>
        )}
        <div className={containerClasses}>
          {textArea ? (
            <textarea
              ref={ref as React.Ref<HTMLTextAreaElement>}
              id={id}
              className={inputClasses}
              disabled={disabled}
              data-textarea='true'
              value={value}
              onChange={onChange as React.ChangeEventHandler<HTMLTextAreaElement>}
              {...(props as Omit<TextAreaProps, 'value' | 'onChange'>)}
            />
          ) : hasMask ? (
            <MaskedInput
              ref={ref as any}
              id={id}
              className={inputClasses}
              disabled={disabled}
              value={value}
              type={computedType}
              onChange={onChange as React.ChangeEventHandler<HTMLInputElement>}
              {...maskProps}
              {...(restProps as any)}
            />
          ) : (
            <input
              ref={ref as React.Ref<HTMLInputElement>}
              id={id}
              className={inputClasses}
              disabled={disabled}
              value={value}
              type={computedType}
              onChange={onChange as React.ChangeEventHandler<HTMLInputElement>}
              {...(restProps as any)}
            />
          )}
          {showClear && (
          <button
            type='button'
            className={styles.clearButton}
            tabIndex={-1}
            onClick={onClear}
            aria-label='Clear input'
          >
            ×
          </button>
          )}

          {showIcon && getIconName() && (
            <Icon
              name={getIconName()}
              className={clsx(styles.inputIcon, getIconClassName(), iconClassName)}
              style={{ fontSize: '22px' }}
            />
          )}
          {isSearch && (
            <Search
              className={clsx(styles.searchIcon)}
            />
          )}
          {isPassword && !textArea && (
            <button
              type='button'
              className={styles.passwordToggle}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              onClick={() => setShowPassword((s) => !s)}
              tabIndex={-1}
            >
              <Icon name={showPassword ? 'visibility' : 'visibility_off'} />
            </button>
          )}
        </div>
        {errorText && (
          <Text
            tag='p'
            className={styles.errorText}
          >
            {errorText}
          </Text>
        )}
      </div>
    );
  },
);

export default Input;
